import { useFormikContext } from 'formik';
import defaultTo from 'lodash/defaultTo';
import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { v1 as uuidV1 } from 'uuid';

import { useFormikUnsavedChangesGuardContext } from './FormikUnsavedChangesGuardContext';

export type FormikUnsavedChangesGuardProps = PropsWithChildren<
  Readonly<{ when?: boolean; onDiscardAndLeave?: () => void }>
>;

/**
 * A wrapper component that registers the form within the FormikUnsavedChangesGuardContext.
 *
 * This component should be used with `enableReinitialize: true.`
 *
 * If you need to use it without enableReinitialize, please make sure to handle the form reset manually after each form submit.
 *
 * @example
 * <FormikUnsavedChangesGuardContextProvider>
 *   <FormikProvider value={formik}>
 *     <FormUnsavedChangesGuard>
 *       // Your form fields goes here
 *     </FormUnsavedChangesGuard>
 *   </FormikProvider>
 * </FormikUnsavedChangesGuardContextProvider>
 */
export const FormikUnsavedChangesGuard: React.FC<FormikUnsavedChangesGuardProps> = ({
  onDiscardAndLeave,
  when,
  children,
}) => {
  // Using uuidV1 as it is based on time, so we can sort forms based on ids
  const formId = useMemo(() => uuidV1(), []);
  const formikContext = useFormikContext();
  const { registerForm, unregisterForm } = useFormikUnsavedChangesGuardContext();

  useEffect(() => {
    registerForm(formId, {
      dirty: defaultTo(when, formikContext.dirty),
      isValid: formikContext.isValid,
      isSubmitting: formikContext.isSubmitting,
      handleReset: formikContext.handleReset,
      submitForm: formikContext.submitForm,
      onDiscardAndLeave,
    });

    return () => {
      unregisterForm(formId);
    };
  }, [
    formId,
    formikContext.dirty,
    formikContext.isValid,
    formikContext.isSubmitting,
    formikContext.handleReset,
    formikContext.submitForm,
    registerForm,
    unregisterForm,
    onDiscardAndLeave,
    when,
  ]);

  return <React.Fragment>{children}</React.Fragment>;
};
